import React, { useState, useEffect } from "react";
import {
  BANNER_ACTIONS_HEADER_MAP,
  BANNER_CARD_ENTITIES,
  BANNER_CARD_ENTITY_VALUE_MAP,
  BANNER_CARD_LANGUAGES,
  BANNER_DETAILS_ACTIONS,
  BANNER_MEDIA_TYPE,
  BANNER_REDIRECTION_TYPES,
  BANNER_REDIRECTION_TYPES_MAP,
  BANNER_SCREEN_LOCATIONS,
  BANNER_SCREEN_LOCATIONS_MAP,
  BANNER_STATUS,
} from "../constants";
import styled from "styled-components";
import BackThick from "sushiweb/Icons/all/BackThick";
import Edit from "sushiweb/Icons/all/AddEdit";
import Delete from "sushiweb/Icons/all/Delete";
import Add from "sushiweb/Icons/all/PlusCircle";

import { useLocation, useHistory } from "react-router-dom";
import DriverBanner from "../../../services/api/DriverBanner";
import UploadImageWrapper from "../common/ImageUploadWrapper";
import ImageUploader from "../../Common/ImageUploader";
import AddLanguageDialog from "../AddEntityDialog/AddLanguage";
import AddScreenDialog from "../AddEntityDialog/AddScreen";
import { useDispatch } from "react-redux";
import { setStatus } from "../../../store/actions/User";
import { fromISOFormat, toISOFormat } from "../helper";
import NotesBar from "../Notes";

const BannerDetailsForm = (props) => {
  const { bannerDetails, userAction } = props;
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const dispatch = useDispatch();
  const [openLanguageSelectionDialog, setOpenLanguageSelectionDialog] =
    useState(false);
  const [openScreenSelectionDialog, setOpenScreenSelectionDialog] =
    useState(false);

  const [formFieldErrors, setFormFieldErrors] = useState({});
  const history = useHistory();
  const [isDisabled, setIsDisabled] = useState(false);
  const [formValues, setFormValues] = useState({
    name: bannerDetails?.name || "",
    start_time: toISOFormat(bannerDetails?.start_time) || "",
    expiry_time: toISOFormat(bannerDetails?.expiry_time) || "",
    priority: bannerDetails?.priority || 0,
    banner_status: bannerDetails?.banner_status || BANNER_STATUS?.[0],
    redirection: bannerDetails?.redirection || {
      redirect_type: BANNER_REDIRECTION_TYPES?.[0],
      redirect_url: "",
    },

    media: bannerDetails?.media || [
      {
        lang_code: BANNER_CARD_LANGUAGES?.[0]?.code,
        media_type: BANNER_MEDIA_TYPE?.[0],
        url: "",
        call_to_action: "",
      },
    ],
    screen_locations: bannerDetails?.screen_locations || [
      {
        screen_section_name: BANNER_SCREEN_LOCATIONS?.[0],
      },
    ],
  });

  const [fileNames, setFileNames] = useState(
    Array(formValues.media.length).fill("")
  );

  const handleValidateFormFields = () => {
    const errors = {};

    // for name field
    if (!formValues?.name) {
      errors["name"] = "Name is required";
    }

    // for start time field
    if (!formValues?.start_time) {
      errors["start_time"] = "Start time is required";
    }

    // for expiry time field
    if (!formValues?.expiry_time) {
      errors["expiry_time"] = "Expiry time is required";
    }

    // for priority field
    if (
      !formValues?.priority ||
      isNaN(formValues?.priority) ||
      formValues?.priority < 1 ||
      formValues?.priority > 5000
    ) {
      errors["priority"] =
        "Priority is required and should be greater than or equal to 1 and less than or equal to 5000";
    }

    // for redirection field
    if (!formValues?.redirection.redirect_type) {
      errors["redirection"] = errors["redirection"] || {};
      errors["redirection"]["redirect_type"] = "Redirect type is required";
    }

    // for redirection url field
    const url_regex = new RegExp("^(http|https)://[^\\s/$.?#].[^\\s]*$", "i");

    if (
      (!formValues?.redirection.redirect_url ||
      (formValues?.redirection?.redirect_type ===
        BANNER_REDIRECTION_TYPES_MAP.EXTERNAL &&
        !url_regex.test(formValues?.redirection.redirect_url))) &&
        formValues?.redirection?.redirect_type !== BANNER_REDIRECTION_TYPES_MAP.NONE
    ) {
      errors["redirection"] = errors["redirection"] || {};
      errors["redirection"]["redirect_url"] =
        "Redirect url is required or invalid";
    }

    // for media field
    const mediaArray = formValues?.media || [];
    for (let index = 0; index < mediaArray.length; index++) {
      const media = mediaArray[index];
      const mediaErrors = {};
      if (!media.url) {
        mediaErrors["url"] = "Media url is required";
      }
      if (!media.lang_code) {
        mediaErrors["lang_code"] = "Language is required";
      }
      if (Object.keys(mediaErrors).length > 0) {
        errors[`media_${index}`] = mediaErrors;
      }
    }

    // for screen locations field
    const screenArray = formValues?.screen_locations || [];
    for (let index = 0; index < screenArray.length; index++) {
      const screen = screenArray[index];
      const screenErrors = {};

      if (!screen.screen_section_name) {
        screenErrors["screen_section_name"] = "Screen location is required";
      }
      if (Object.keys(screenErrors).length > 0) {
        errors[`screen_${index}`] = screenErrors;
      }
    }

    return errors;
  };

  const handleMediaChange = (file, index, url) => {
    if (file) {
      setFileNames((prevFileNames) => [
        ...prevFileNames.slice(0, index),
        file.name,
        ...prevFileNames.slice(index + 1),
      ]);

      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        media: [
          ...prevFormValues.media.slice(0, index),
          {
            ...prevFormValues.media[index],
            url: url,
          },
          ...prevFormValues.media.slice(index + 1),
        ],
      }));
    }
  };

  const handleClickBack = () => {
    history.goBack();
  };

  const handleEdit = () => {
    const params = new URLSearchParams(location.search);
    params.set("action", BANNER_DETAILS_ACTIONS.EDIT);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const handleCardAddition = (entity_name, entity_value) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [entity_name]: [
        ...prevFormValues[entity_name],
        {
          ...BANNER_CARD_ENTITY_VALUE_MAP[entity_name],
          [entity_name === BANNER_CARD_ENTITIES.media
            ? "lang_code"
            : "screen_section_name"]: entity_value,
        },
      ],
    }));
  };

  const handleCardRemoval = (entity_name, index) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [entity_name]: [
        ...prevFormValues[entity_name].slice(0, index),
        ...prevFormValues[entity_name].slice(index + 1),
      ],
    }));
  };

  const handleInputChange = (event, entity_name, entity_type, index) => {
    // positive values for index means it is an array of objects , negative means it is a single object and -2 means it is a single object inside an object
    const value = event.target.value;
    if (index === -1) {
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        [entity_name]: value,
      }));
    } else if (index === -2) {
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        [entity_name]: {
          ...prevFormValues[entity_name],
          [entity_type]: value,
        },
      }));
    } else {
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        [entity_name]: [
          ...prevFormValues[entity_name].slice(0, index),
          {
            ...prevFormValues[entity_name][index],
            [entity_type]: value,
          },
          ...prevFormValues[entity_name].slice(index + 1),
        ],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = handleValidateFormFields();

    if (Object.keys(errors).length > 0) {
      dispatch(
        setStatus({
          message: "Please fill all the required fields",
          type: "error",
        })
      );
      setFormFieldErrors(errors);
      return;
    } else {
      setFormFieldErrors({});
    }

    // convert the form values to the required format
    const payload = {
      ...formValues,
      priority: parseInt(formValues.priority),
      start_time: fromISOFormat(formValues.start_time),
      expiry_time: fromISOFormat(formValues.expiry_time),
    };
    try {
      if (userAction === BANNER_DETAILS_ACTIONS.EDIT) {
        const response = await DriverBanner.editBanner({
          ...payload,
          banner_id: bannerDetails?.banner_id || "",
        });
        const { error } = response;

        if (!error) {
          dispatch(
            setStatus({
              message: "Banner updated successfully",
              type: "success",
            })
          );
          history.push(
            "/banner/details?id=" + bannerDetails?.banner_id + "&action=view"
          );
        }
        return;
      } else {
        const response = await DriverBanner.createBanner(payload);
        const { banner_id } = response;

        if (banner_id) {
          dispatch(
            setStatus({
              message: "Banner created successfully",
              type: "success",
            })
          );
          history.push(
            "/banner/target_audience?id=" + banner_id + "&action=create"
          );
        }
        return;
      }
    } catch (error) {
      dispatch(
        setStatus({
          message: "Something went wrong, please try again",
          type: "error",
        })
      );

      console.log(error);
    }
  };

  useEffect(() => {
    if (query.get("action") === BANNER_DETAILS_ACTIONS.EDIT) {
      setIsDisabled(false);
    }
    if (userAction === BANNER_DETAILS_ACTIONS.CREATE) {
      setIsDisabled(false);
    }
  }, [location]);

  useEffect(() => {
    setFormValues({
      name: bannerDetails?.name || "",
      start_time: toISOFormat(bannerDetails?.start_time) || "",
      expiry_time: toISOFormat(bannerDetails?.expiry_time) || "",
      priority: bannerDetails?.priority || 0,
      banner_status: bannerDetails?.banner_status || BANNER_STATUS?.[0],
      redirection: bannerDetails?.redirection || {
        redirect_type: BANNER_REDIRECTION_TYPES?.[0],
        redirect_url: "",
      },

      media: bannerDetails?.media || [
        {
          lang_code: BANNER_CARD_LANGUAGES?.[0]?.code,
          media_type: BANNER_MEDIA_TYPE?.[0],
          url: "",
          call_to_action: "",
        },
      ],
      screen_locations: bannerDetails?.screen_locations || [
        {
          screen_section_name: BANNER_SCREEN_LOCATIONS?.[0],
        },
      ],
    });
  }, [bannerDetails]);

  return (
    <>
      <BannerFormContainer>
        <BannerFormHeader>
          <BannerFormHeaderLeft>
            <BackButton color="#828282" onClick={handleClickBack} />{" "}
            {BANNER_ACTIONS_HEADER_MAP[userAction] || "BANNER DETAILS"}
          </BannerFormHeaderLeft>
          <BannerFormHeaderRight>
            {userAction === BANNER_DETAILS_ACTIONS.VIEW ? (
              <ButtonOutlined onClick={handleEdit}>
                <Edit color="#ff5a5f" />
                Edit
              </ButtonOutlined>
            ) : (
              <ButtonFilled
                type="submit"
                onClick={handleSubmit}
                disabled={isDisabled}
              >
                Save
              </ButtonFilled>
            )}
          </BannerFormHeaderRight>
        </BannerFormHeader>
        {/* show banner_id and updated_by  as a info */}
        {userAction !== BANNER_DETAILS_ACTIONS.CREATE && (
          <BannerInfoContainer>
            <BannerInfo>
              <BannerInfoLabel>Banner ID :</BannerInfoLabel>
              <BannerInfoValue>{bannerDetails?.banner_id}</BannerInfoValue>
            </BannerInfo>
            <BannerInfo>
              <BannerInfoLabel>Updated By :</BannerInfoLabel>
              <BannerInfoValue>{bannerDetails?.updated_by}</BannerInfoValue>
            </BannerInfo>
          </BannerInfoContainer>
        )}
        <BannerFormFieldContainer>
          <BannerFormField
            disabled={userAction !== BANNER_DETAILS_ACTIONS.CREATE}
          >
            <BannerFormFieldLabel>Name</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormFieldTextInput
                name="name"
                type="text"
                value={formValues.name}
                onChange={(e) => handleInputChange(e, "name", "", -1)}
              />
              {formFieldErrors?.name && (
                <BannerFormFieldError>
                  {formFieldErrors?.name}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>
          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>Start date time</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormDateInput
                type="datetime-local"
                value={formValues.start_time}
                onChange={(e) => handleInputChange(e, "start_time", "", -1)}
                min={new Date().toISOString().split("T")[0]}
                name="start_time"
              />
              {formFieldErrors?.start_time && (
                <BannerFormFieldError>
                  {formFieldErrors?.start_time}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>
          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>End date time</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormDateInput
                name="expiry_time"
                value={formValues.expiry_time}
                onChange={(e) => handleInputChange(e, "expiry_time", "", -1)}
                min={new Date().toISOString().split("T")[0]}
                type="datetime-local"
                defaultValue="2021-08-01T00:00"
              />
              {formFieldErrors?.expiry_time && (
                <BannerFormFieldError>
                  {formFieldErrors?.expiry_time}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>

          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>Redirected view to</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormFieldSelectInput
                value={formValues.redirection.redirect_type}
                onChange={(e) =>
                  handleInputChange(e, "redirection", "redirect_type", -2)
                }
                name="redirect_type"
                defaultValue={formValues.redirection.redirect_type}
              >
                {BANNER_REDIRECTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </BannerFormFieldSelectInput>
              {formFieldErrors?.redirection?.redirect_type && (
                <BannerFormFieldError>
                  {formFieldErrors?.redirection?.redirect_type}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>

          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>External url</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormFieldTextInput
                disabled = {formValues.redirection.redirect_type == BANNER_REDIRECTION_TYPES_MAP.NONE}
                type="text"
                value={formValues.redirection.redirect_url}
                onChange={(e) =>
                  handleInputChange(e, "redirection", "redirect_url", -2)
                }
                name="redirect_url"
              />
              {formFieldErrors?.redirection?.redirect_url && (
                <BannerFormFieldError>
                  {formFieldErrors?.redirection?.redirect_url}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>

          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>Status</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormFieldSelectInput
                value={formValues.banner_status}
                defaultValue={formValues.banner_status}
                onChange={(e) => handleInputChange(e, "banner_status", "", -1)}
                name="banner_status"
              >
                {BANNER_STATUS.map((status) => (
                  <option key={status} value={status}>
                    {status?.split("_")?.[2]}
                  </option>
                ))}
              </BannerFormFieldSelectInput>
              {formFieldErrors?.banner_status && (
                <BannerFormFieldError>
                  {formFieldErrors?.banner_status}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>

          <BannerFormField
            disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
          >
            <BannerFormFieldLabel>Priority</BannerFormFieldLabel>
            <BannerFormFieldInputContainer>
              <BannerFormFieldTextInput
                value={formValues.priority}
                onChange={(e) => handleInputChange(e, "priority", "", -1)}
                name="priority"
                type="number"
              />
              {formFieldErrors?.priority && (
                <BannerFormFieldError>
                  {formFieldErrors?.priority}
                </BannerFormFieldError>
              )}
            </BannerFormFieldInputContainer>
          </BannerFormField>
        </BannerFormFieldContainer>
        <BannerFormHeader>
          <BannerFormHeaderLeft>LANGUAGE SELECTION</BannerFormHeaderLeft>
        </BannerFormHeader>

        <NotesBar
          instructions={[
            "For Gigs: 1008 × 450 (resolution)",
            "For Bazaar and Comms hub : 336×152 (resolution)",
          ]}
        />
        {formValues?.media.map((media, index) => (
          <BannerFormFieldCard key={index}>
            <BannerFormField
              disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
            >
              <BannerFormFieldLabel>Language</BannerFormFieldLabel>
              <BannerFormFieldInputContainer>
                <BannerFormFieldSelectInput
                  defaultValue={media.lang_code}
                  value={media.lang_code}
                  onChange={(e) =>
                    handleInputChange(e, "media", "lang_code", index)
                  }
                  name="lang_code"
                >
                  {BANNER_CARD_LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.name} - {language.code}
                    </option>
                  ))}
                </BannerFormFieldSelectInput>
                {formFieldErrors?.[`media_${index}`]?.["lang_code"] && (
                  <BannerFormFieldError>
                    {formFieldErrors?.[`media_${index}`]?.["lang_code"]}
                  </BannerFormFieldError>
                )}
              </BannerFormFieldInputContainer>
            </BannerFormField>
            <BannerFormField
              disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
            >
              <BannerFormFieldLabel>
                <UploadImageWrapper
                  UploadComponent={ImageUploader}
                  onUpload={({ url, file }) =>
                    handleMediaChange(file, index, url)
                  }
                  uploadComponentProps={{
                    maxSize: 0.5,
                  }}
                  url={media.url}
                />
              </BannerFormFieldLabel>
              <span>{fileNames?.[index]}</span>
              {formFieldErrors?.[`media_${index}`]?.["url"] && (
                <BannerFormFieldError>
                  {formFieldErrors?.[`media_${index}`]?.["url"]}
                </BannerFormFieldError>
              )}
            </BannerFormField>

            <BannerFormField
              disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
            >
              <BannerFormFieldLabel>CTA</BannerFormFieldLabel>
              <BannerFormFieldInputContainer>
                <BannerFormFieldTextInput
                  name="call_to_action"
                  value={media.call_to_action}
                  onChange={(e) =>
                    handleInputChange(e, "media", "call_to_action", index)
                  }
                  type="text"
                />
                {formFieldErrors?.[`media_${index}`]?.["call_to_action"] && (
                  <BannerFormFieldError>
                    {formFieldErrors?.[`media_${index}`]?.["call_to_action"]}
                  </BannerFormFieldError>
                )}
              </BannerFormFieldInputContainer>
            </BannerFormField>

            {
              // index === formValues.media.length - 1 &&
              userAction !== BANNER_DETAILS_ACTIONS.VIEW && (
                <BannerFormFieldCardFooter>
                  <BannerFormCardRemoveButton
                    onClick={() =>
                      handleCardRemoval(BANNER_CARD_ENTITIES.media, index)
                    }
                    disabled={formValues?.media?.length === 1}
                  >
                    <Delete
                      color={`${
                        formValues?.media?.length === 1 ? "#ccc" : "#ff5a5f"
                      }`}
                    />
                    Remove
                  </BannerFormCardRemoveButton>
                  <BannerFormCardActionSeparator />
                  <BannerFormCardAddButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenLanguageSelectionDialog(true);
                    }}
                  >
                    <Add color="#ff5a5f" />
                    Add new language
                  </BannerFormCardAddButton>
                </BannerFormFieldCardFooter>
              )
            }
          </BannerFormFieldCard>
        ))}
        <BannerFormHeader>
          <BannerFormHeaderLeft>SCREEN LOCATIONS</BannerFormHeaderLeft>
        </BannerFormHeader>
        {formValues?.screen_locations.map((screen_locations, index) => (
          <BannerFormFieldCard key={index}>
            <BannerFormField
              disabled={userAction === BANNER_DETAILS_ACTIONS.VIEW}
            >
              <BannerFormFieldLabel>Screen Location</BannerFormFieldLabel>
              <BannerFormFieldInputContainer>
                <BannerFormFieldSelectInput
                  defaultValue={screen_locations?.screen_section_name}
                  value={screen_locations?.screen_section_name}
                  onChange={(e) =>
                    handleInputChange(
                      e,
                      "screen_locations",
                      "screen_section_name",
                      index
                    )
                  }
                  name="screen_section_name"
                >
                  {BANNER_SCREEN_LOCATIONS.map((screen) => (
                    <option key={screen} value={screen}>
                      {BANNER_SCREEN_LOCATIONS_MAP[screen]}
                    </option>
                  ))}
                </BannerFormFieldSelectInput>
                {formFieldErrors?.[`screen_${index}`]?.[
                  "screen_section_name"
                ] && (
                  <BannerFormFieldError>
                    {
                      formFieldErrors?.[`screen_${index}`]?.[
                        "screen_section_name"
                      ]
                    }
                  </BannerFormFieldError>
                )}
              </BannerFormFieldInputContainer>
            </BannerFormField>

            {
              // index === formValues.screen_locations.length - 1 &&
              userAction !== BANNER_DETAILS_ACTIONS.VIEW && (
                <BannerFormFieldCardFooter>
                  <BannerFormCardRemoveButton
                    onClick={() =>
                      handleCardRemoval(
                        BANNER_CARD_ENTITIES.screen_locations,
                        index
                      )
                    }
                    disabled={formValues?.screen_locations?.length === 1}
                  >
                    <Delete
                      color={`${
                        formValues?.screen_locations?.length === 1
                          ? "#ccc"
                          : "#ff5a5f"
                      }`}
                    />
                    Remove
                  </BannerFormCardRemoveButton>
                  <BannerFormCardActionSeparator />
                  <BannerFormCardAddButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenScreenSelectionDialog(true);
                    }}
                  >
                    <Add color="#ff5a5f" />
                    Add additional screen
                  </BannerFormCardAddButton>
                </BannerFormFieldCardFooter>
              )
            }
          </BannerFormFieldCard>
        ))}
      </BannerFormContainer>

      <AddLanguageDialog
        options={BANNER_CARD_LANGUAGES.map((language) => ({
          label: `${language.name} - ${language.code}`,
          value: language.code,
        }))}
        open={openLanguageSelectionDialog}
        setOpen={setOpenLanguageSelectionDialog}
        onSelect={(language) => {
          const languagesAlreadyAdded = formValues?.media?.map(
            (item) => item.lang_code
          );

          if (languagesAlreadyAdded.includes(language)) {
            dispatch(
              setStatus({
                message:
                  "this language is already added, please select a different language",
                type: "error",
              })
            );
          } else {
            handleCardAddition(BANNER_CARD_ENTITIES.media, language);
            dispatch(
              setStatus({
                message: "language added",
                type: "success",
              })
            );
          }
        }}
      />

      <AddScreenDialog
        open={openScreenSelectionDialog}
        setOpen={setOpenScreenSelectionDialog}
        options={BANNER_SCREEN_LOCATIONS.map((screen) => ({
          label: BANNER_SCREEN_LOCATIONS_MAP?.[screen],
          value: screen,
        }))}
        onSelect={(screen) => {
          const screensAlreadyAdded = formValues?.screen_locations?.map(
            (item) => item.screen_section_name
          );

          if (screensAlreadyAdded.includes(screen)) {
            dispatch(
              setStatus({
                message:
                  "this screen is already added, please select a different language",
                type: "error",
              })
            );
          } else {
            handleCardAddition(BANNER_CARD_ENTITIES.screen_locations, screen);
            dispatch(
              setStatus({
                message: "screen added",
                type: "success",
              })
            );
          }
        }}
      />
    </>
  );
};

const BannerFormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const BannerFormHeader = styled.h1`
  font-size: 18px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #828282;
  gap: 12px;
`;

const BannerFormHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerFormHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonFilled = styled.button`
  background-color: #ff5a5f;
  color: #fff;
  padding: 8px 32px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonOutlined = styled.button`
  background-color: #fff;
  color: #ff5a5f;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #ff5a5f;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
`;

const BackButton = styled(BackThick)`
  cursor: pointer;
`;

const BannerFormFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BannerFormFieldInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  width: 80%;
  position: relative;
`;

const BannerFormField = styled.fieldset`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  :disabled {
    opacity: 0.75;
  }
`;

const BannerFormFieldLabel = styled.label`
  font-size: 16px;
  width: 20%;
  font-weight: 500;
`;

const BannerFormFieldError = styled.span`
  color: red;
`;

const BannerFormFieldTextInput = styled.input`
  padding: 8px;
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 8px;
`;

const BannerFormDateInput = styled.input`
  padding: 8px;
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 8px;
`;

const BannerFormFieldSelectInput = styled.select`
  padding: 8px;
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 8px;
  background-color: #fff;
  color: #000;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &:hover {
    background-color: #f5f5f5;
  }
  // target the arrow
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23000' width='18px' height='18px'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px top 50%;
  background-size: 18px;
  padding-right: 24px;
  // target the dropdown list
`;

const BannerFormFieldCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid #dddddd;
  border-radius: 8px;
`;

const BannerFormFieldCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BannerFormCardRemoveButton = styled.button`
  color: #ff5a5f;
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  background-color: #fafafa;
  :disabled {
    cursor: not-allowed;
    color: #ccc;
  }
`;

const BannerFormCardActionSeparator = styled.div`
  width: 1px;
  height: 100%;
  background-color: #dddddd;
  margin: 0 8px;
`;

const BannerFormCardAddButton = styled.button`
  color: #ff5a5f;
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fafafa;
  font-size: 16px;
`;

const BannerInfoContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  padding: 16px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  background-color: #fafafa;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
`;

const BannerInfo = styled.div`
  display: flex;
  gap: 8px;
`;

const BannerInfoLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const BannerInfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
`;

export default BannerDetailsForm;
